import React, { useState } from "react";
import "./App.css";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import Footer from "./components/Footer";
import ExplorePage from "./components/ExplorePage";
import JsonEditor from "./components/JsonEditor";
import ReportPage from "./components/ReportPage";
import { Route, Routes, useNavigate } from "react-router-dom";
import Documentation from './components/Documentation';
import ApplicationSettings from './components/ApplicationSettings';
import ReportsPage from './components/ReportsPage';
import DecisionDocumentation from './components/DecisionDocumentation';
import Dashboard from './components/Dashboard';
import HelpPage from './components/HelpPage';
import LogsPage from "./components/LogsPage";
import LoginPage from "./components/LoginPage";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "./firebase";
import { isEmailAuthorized } from "./config/auth";
import UpdatesPage from './components/UpdatesPage';
import Version2 from "./components/Version2";
import NewEditor from "./components/NewEditor";
import DatabasePage from "./components/DatabasePage";
import HomePage from "./components/HomePage";
import DigitalVersion2 from "./components/DigitalVersion2";
import DigitalEditor from "./components/DigitalEditor";
import Demo from "./components/Demo";
import ReportDocumentation from "./components/ReportDocumentation";
import TaskTracking from "./components/TaskTracking";
import OrgManagement from "./components/OrgManagement";
import ReviewToolManagement from "./components/ReviewToolManagement";
import CustomerDueDiligence from "./components/CustomerDueDiligence";
import ObservatoryAccessManagement from "./components/ObservatoryAccessManagement";
import ReportToolManagement from "./components/ReportToolManagement";
import TikTokAnalysis from "./components/TikTokAnalysis";
import DocumentationV2 from "./components/DocumentationV2";
import EvidenceTrailManagement from "./components/EvidenceTrailManagement";
import SOTATracking from "./components/SOTATracking";
import StackObservatory from "./components/StackObservatory";
import PerformanceReviewedSessionManagement from "./components/PerformanceReviewedSessionManagement";
import TechRoadmap from "./components/TechRoadmap";

function App() {
  const [treeData, setTreeData] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const [user, authLoading, authError] = useAuthState(auth);

  // Authentication Guard: Check if user is authenticated and authorized
  if (authLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show login page if user is not authenticated
  if (!user) {
    return <LoginPage />;
  }

  // Check if authenticated user's email is authorized
  if (user && !isEmailAuthorized(user.email)) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="alert alert-danger text-center" role="alert">
          <h4 className="alert-heading">Access Denied</h4>
          <p>Your email address ({user.email}) is not authorized to access this application.</p>
          <hr />
          <p className="mb-0">Please contact your administrator for access.</p>
        </div>
      </div>
    );
  }

  const handleTabChange = (tab) => {
    switch (tab) {
      case 'explore':
        navigate('/');
        break;
      case 'viewJson':
        navigate('/view-json');
        break;
      case 'editJson':
        navigate('/edit-json');
        break;
      case 'report':
        navigate('/report');
        break;
      default:
        navigate('/');
        break;
    }
  };

  const handleGenerateProperties = async (data, folderName) => {
    try {
      setLoading(true);
      setErrorMessage("");
      const response = await fetch("https://reactserver-757095405629.us-central1.run.app/api/generate-all", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_name: folderName,
          images: data,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const tree = await response.json();
      setTreeData(tree);
      setSelectedFolder(folderName);
      navigate('/edit-json'); // Navigate to edit-json after successful generation
    } catch (error) {
      console.error("Error generating properties:", error);
      if (error.message.includes("500")) {
        setErrorMessage("Failed to generate properties. Please try again later.");
      } else {
        setErrorMessage("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateJsonData = (updatedJsonData) => {
    setTreeData(updatedJsonData);
  };

  const navigateToReportPage = () => {
    navigate('/report', { state: { jsonData: treeData } });
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="App">
      <Header toggleSidebar={toggleSidebar} />
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/edit-json" element={
            <JsonEditor
              folderName={selectedFolder}
              jsonData={treeData}
              navigateToReport={navigateToReportPage}
              onUpdateJsonData={handleUpdateJsonData}
            />
          } />
          <Route path="/report" element={<ReportPage jsonData={treeData} />} />
          <Route path="/documentation/*" element={<Documentation />} />
          <Route path="/documentation-v2" element={<DocumentationV2 />} />
          <Route path="/settings" element={<ApplicationSettings />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/logs" element={<LogsPage />} />
          <Route path="/updates" element={<UpdatesPage />} />
          <Route path="/version2" element={<Version2 />} /> {/* Dark Pattern Analysis */}
          <Route path="/digital-version2" element={<DigitalVersion2 />} /> {/* Digital Violation Analysis */}
          <Route path="/database" element={<DatabasePage />} /> {/* Add route for DatabasePage */}
          {/* <Route path="/decision-documentation" element={<DecisionDocumentation />} /> */}
          <Route path="/new-editor" element={<NewEditor />} /> {/* Dark Pattern Editor */}
          <Route path="/digital-editor" element={<DigitalEditor />} /> {/* Digital Violation Editor */}
          <Route path="/demo" element={<Demo />} /> {/* Demo Environment */}
          <Route path="/task-tracking" element={<TaskTracking />} /> {/* Task Tracking System */}
          <Route path="/org-management" element={<OrgManagement />} /> {/* Organization Management */}
          <Route path="/review-tool-management" element={<ReviewToolManagement />} /> {/* Review Tool Management */}
          <Route path="/report-tool-management" element={<ReportToolManagement />} /> {/* Report Tool Management */}
          <Route path="/observatory-access-management" element={<ObservatoryAccessManagement />} /> {/* Observatory Tool Access Management */}
          <Route path="/evidence-trail-management" element={<EvidenceTrailManagement />} /> {/* Evidence Trail Access Management */}
          <Route path="/report-documentation" element={<ReportDocumentation />} />
          <Route path="/customer-due-diligence" element={<CustomerDueDiligence />} />
          <Route path="/tiktok-analysis" element={<TikTokAnalysis />} />
          <Route path="/sota-tracking" element={<SOTATracking />} /> {/* SOTA Research Tracking */}
          <Route path="/stack-observatory" element={<StackObservatory />} /> {/* Stack Observatory System */}
          <Route path="/performance-reviewed-sessions" element={<PerformanceReviewedSessionManagement />} /> {/* Performance Reviewed Session Management */}
          <Route path="/tech-roadmap" element={<TechRoadmap />} /> {/* Tech Roadmap */}
        </Routes>
      </main>
      {/* <Footer/> */}
    </div>
  );
}

export default App;
